import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import {
  FiRadio, FiCheck, FiList, FiShield, FiSearch, FiStar,
  FiGlobe, FiExternalLink,
} from 'react-icons/fi';
import Reel from '../components/Reel';
import CleanVideo from '../components/CleanVideo';
import ChromeStoreBtn from '../components/ChromeStoreBtn';
import { getProduct } from '../data/catalog';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="group border border-border bg-card p-6 transition-colors hover:border-accent-indigo/50">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-accent-indigo/10 transition-transform group-hover:scale-110">
      <SafeIcon icon={icon} className="h-6 w-6 text-accent-indigo" />
    </div>
    <h3 className="mb-2 font-display text-xl font-bold text-white">{title}</h3>
    <p className="text-sm leading-relaxed text-muted">{desc}</p>
  </div>
);

const SpecItem = ({ title, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border py-3">
    <span className="shrink-0 text-sm text-muted">{title}</span>
    <span className="text-right font-mono text-sm text-primary">{value}</span>
  </div>
);

const MyOnlineRadio = () => {
  const PRODUCT = getProduct('my-online-radio');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="border-b border-border pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent-indigo/20 bg-accent-indigo/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-accent-indigo">
              <SafeIcon icon={FiRadio} /> Chrome extension
            </div>
            <h1 className="mb-6 font-display text-5xl font-extrabold tracking-tightest text-white md:text-7xl">
              Search the world's stations.<br />
              <span className="text-accent-indigo">Press play in one click.</span>
            </h1>
            <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-muted">
              My Online Radio searches the community-run Radio Browser catalog, 58,000+ internet
              radio stations from every country, and streams whichever one you pick straight from
              the toolbar. No accounts, no ads, no sign-in.
            </p>

            <div className="flex flex-col items-center gap-5">
              <ChromeStoreBtn accentColor="bg-accent-indigo hover:bg-accent-indigo/90" className="px-10 py-4 scale-110" href={PRODUCT.storeLink} />
              <span className="max-w-sm text-sm text-muted">
                <strong className="text-white">Free</strong>, no ads, no subscription.
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trailer */}
      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <p className="rule-label mb-6">Trailer — My Online Radio</p>
          <CleanVideo src="myonlineradio.mp4" title="My Online Radio Trailer" />
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — My Online Radio</p>
          <Reel frames={PRODUCT.frames} label="My Online Radio" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-border bg-card/30 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold text-white">One catalog, every country</h2>
            <p className="text-muted">Search it, favorite it, and it keeps playing after the popup closes.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={FiSearch}
              title="Search as you type"
              desc="Search by station name or filter by country against the live Radio Browser catalog. The Popular tab opens on the current top-voted stations worldwide."
            />
            <FeatureCard
              icon={FiStar}
              title="Favorites, saved locally"
              desc="Star a station and it is saved on this device, ready to play again from the Favorites tab. Nothing about your favorites is sent anywhere."
            />
            <FeatureCard
              icon={FiRadio}
              title="Keeps playing, popup closed"
              desc="Playback runs in a background document, not the popup, so closing the popup to check something else never cuts the stream."
            />
            <FeatureCard
              icon={FiShield}
              title="Three fixed API hosts"
              desc="Host permissions name exactly three Radio Browser mirrors. The extension cannot read or touch anything on the pages you visit."
            />
          </div>
        </div>
      </section>

      {/* The honest bit */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-20 lg:flex-row">
            <div className="flex-1 space-y-8">
              <h2 className="font-display text-4xl font-bold leading-tight text-white">
                Where the stream actually<br />
                <span className="text-accent-indigo">comes from, and where it does not.</span>
              </h2>
              <p className="text-muted">
                Searching and browsing stations talks to Radio Browser, a community-run, free
                database of internet radio streams. Picking a station then opens a direct
                connection from your browser to that station's own streaming server — the same as
                opening its stream URL in a tab. Neither we nor Radio Browser sit in between.
              </p>
              <p className="text-muted">
                Playing a station reports one anonymous play count back to Radio Browser, the same
                way any radio directory keeps its popularity numbers meaningful. No account, no
                identifier tied to you, nothing else sent.
              </p>
              <div className="space-y-6">
                {[
                  [FiGlobe, 'No host access beyond three API mirrors', 'host_permissions lists exactly the three Radio Browser servers the extension calls. It has no access to any other site.'],
                  [FiList, 'Favorites live in chrome.storage.local', 'A versioned key on your own device. Nothing is synced to an account, because there is no account.'],
                ].map(([icon, t, d]) => (
                  <div key={t} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-indigo/10 text-accent-indigo">
                      <SafeIcon icon={icon} />
                    </div>
                    <p className="text-muted">
                      <strong className="text-white">{t}:</strong> {d}
                    </p>
                  </div>
                ))}
              </div>
              <Link
                to="/my-online-radio/privacy"
                className="inline-flex items-center gap-2 font-medium text-white transition-colors hover:text-accent-indigo"
              >
                Read the privacy policy <SafeIcon icon={FiExternalLink} />
              </Link>
            </div>

            <div className="w-full max-w-md flex-1">
              <div className="border border-border bg-card p-8">
                <h3 className="mb-6 flex items-center gap-2 font-display text-xl font-bold text-white">
                  <SafeIcon icon={FiList} className="text-accent-indigo" /> Tech Specs
                </h3>
                <div className="space-y-1">
                  <SpecItem title="Platform" value="Chrome 109+" />
                  <SpecItem title="Manifest" value="MV3" />
                  <SpecItem title="Permissions" value="storage, offscreen" />
                  <SpecItem title="Host permissions" value="3 Radio Browser mirrors" />
                  <SpecItem title="Station catalog" value="58,000+ (Radio Browser)" />
                  <SpecItem title="Accounts" value="None" />
                </div>
                <div className="mt-8 border-t border-border pt-6">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-bold text-white">Price</span>
                    <span className="font-mono text-xl font-bold text-accent-indigo">{PRODUCT.price}</span>
                  </div>
                  <p className="text-xs text-muted">
                    Free, no ads, no subscription. Available now on the Chrome Web Store.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyOnlineRadio;
